import {
  balanceISOYearMonth,
  balanceISODate,
  balanceTime,
  balanceDuration,
  totalDurationNanoseconds,
  nanosecondsToDays
} from './balance.js'
import { regulateISODate, rejectDuration } from './regulate.js'
import Duration, { durationSign } from '../Duration.js'
import DateTime from '../DateTime.js'
import PlainTime from '../PlainTime.js'
import { getDayOfYear, isLeapYear, getDaysInMonth } from './utils.js'
import { MILLION, BILLION, THOUSAND, ZERO, NEGATIVE_ONE, SIXTY } from './constants.js'
import SlotMap from '@neumatter/slot-map'

const nsPerTimeUnit = {
  hour: 3600e9,
  minute: 60e9,
  second: 1e9,
  millisecond: 1e6,
  microsecond: 1e3,
  nanosecond: 1
}

export function addISODate (
  yearParam,
  monthParam,
  dayParam,
  yearsParam,
  monthsParam,
  weeksParam,
  daysParam
) {
  let year = yearParam
  let month = monthParam
  let day = dayParam
  let years = yearsParam
  let months = monthsParam
  let weeks = weeksParam
  let days = daysParam

  year += years
  month += months
  ;({ year, month } = balanceISOYearMonth(year, month))
  ;({ year, month, day } = regulateISODate(year, month, day))
  days += 7 * weeks
  day += days
  ;({ year, month, day } = balanceISODate(year, month, day))
  return { year, month, day }
}

export function addTime (
  hourParam,
  minuteParam,
  secondParam,
  millisecondParam,
  microsecondParam,
  nanosecondParam,
  hours,
  minutes,
  seconds,
  milliseconds,
  microseconds,
  nanoseconds
) {
  let hour = hourParam
  let minute = minuteParam
  let second = secondParam
  let millisecond = millisecondParam
  let microsecond = microsecondParam
  let nanosecond = nanosecondParam

  hour += hours
  minute += minutes
  second += seconds
  millisecond += milliseconds
  microsecond += microseconds
  nanosecond += nanoseconds

  return balanceTime(hour, minute, second, millisecond, microsecond, nanosecond)
}

export function addDateTime (
  year,
  month,
  day,
  hourParam,
  minuteParam,
  secondParam,
  millisecondParam,
  microsecondParam,
  nanosecondParam,
  calendar,
  years,
  months,
  weeks,
  daysParam,
  hours,
  minutes,
  seconds,
  milliseconds,
  microseconds,
  nanoseconds
) {
  let days = daysParam
  // Add the time part
  const {
    deltaDays,
    hour,
    minute,
    second,
    millisecond,
    microsecond,
    nanosecond
  } = addTime(
    hourParam,
    minuteParam,
    secondParam,
    millisecondParam,
    microsecondParam,
    nanosecondParam,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  )
  days += deltaDays

  // Delegate the date part addition to the calendar
  const datePart = new DateTime(
    year,
    month,
    day,
    0,
    0,
    0,
    0,
    0,
    0,
    null,
    calendar
  )
  const dateDuration = new Duration(
    years,
    months,
    weeks,
    days,
    0,
    0,
    0,
    0,
    0,
    0
  )
  const addedDate = calendar.dateAdd(datePart, dateDuration)
  const addedSlotMap = SlotMap.get(addedDate)

  return {
    year: addedSlotMap.get('year'),
    month: addedSlotMap.get('month'),
    day: addedSlotMap.get('day'),
    hour,
    minute,
    second,
    millisecond,
    microsecond,
    nanosecond
  }
}

function compareISODate (y1, m1, d1, y2, m2, d2) {
  if (y1 !== y2) {
    const value = y1 - y2
    return value < 0 ? -1 : value > 0 ? 1 : 0
  }

  if (m1 !== m2) {
    const value = m1 - m2
    return value < 0 ? -1 : value > 0 ? 1 : 0
  }

  if (d1 !== d2) {
    const value = d1 - d2
    return value < 0 ? -1 : value > 0 ? 1 : 0
  }

  return 0
}

export function differenceISODate (y1, m1, d1, y2, m2, d2, largestUnit) {
  switch (largestUnit) {
    case 'year':
    case 'month': {
      const sign = -compareISODate(y1, m1, d1, y2, m2, d2)
      if (sign === 0) return { years: 0, months: 0, weeks: 0, days: 0 }

      const start = { year: y1, month: m1, day: d1 }
      const end = { year: y2, month: m2, day: d2 }

      let years = end.year - start.year
      let mid = addISODate(y1, m1, d1, years, 0, 0, 0, 'constrain')
      let midSign = -compareISODate(mid.year, mid.month, mid.day, y2, m2, d2)
      if (midSign === 0) {
        return largestUnit === 'year'
          ? { years, months: 0, weeks: 0, days: 0 }
          : { years: 0, months: years * 12, weeks: 0, days: 0 }
      }
      let months = end.month - start.month
      if (midSign !== sign) {
        years -= sign
        months += sign * 12
      }
      mid = addISODate(y1, m1, d1, years, months, 0, 0, 'constrain')
      midSign = -compareISODate(mid.year, mid.month, mid.day, y2, m2, d2)
      if (midSign === 0) {
        return largestUnit === 'year'
          ? { years, months, weeks: 0, days: 0 }
          : { years: 0, months: months + years * 12, weeks: 0, days: 0 }
      }
      if (midSign !== sign) {
        // The end date is later in the month than mid date (or earlier for
        // negative durations). Back up one month.
        months -= sign
        if (months === -sign) {
          years -= sign
          months = 11 * sign
        }
        mid = addISODate(y1, m1, d1, years, months, 0, 0, 'constrain')
      }

      let days = 0
      // If we get here, months and years are correct (no overflow), and `mid`
      // is within the range from `start` to `end`. To count the days between
      // `mid` and `end`, there are 3 cases:
      // 1) same month: use simple subtraction
      // 2) end is previous month from intermediate (negative duration)
      // 3) end is next month from intermediate (positive duration)
      if (mid.month === end.month) {
        // 1) same month: use simple subtraction
        days = end.day - mid.day
      } else if (sign < 0) {
        // 2) end is previous month from intermediate (negative duration)
        // Example: intermediate: Feb 1, end: Jan 30, DaysInMonth = 31, days = -2
        days = -mid.day - (getDaysInMonth(end.year, end.month) - end.day)
      } else {
        // 3) end is next month from intermediate (positive duration)
        // Example: intermediate: Jan 29, end: Feb 1, DaysInMonth = 31, days = 3
        days = end.day + (getDaysInMonth(mid.year, mid.month) - mid.day)
      }

      if (largestUnit === 'month') {
        months += years * 12
        years = 0
      }
      return { years, months, weeks: 0, days }
    }
    case 'week':
    case 'day': {
      let larger, smaller, sign
      if (compareISODate(y1, m1, d1, y2, m2, d2) < 0) {
        smaller = { year: y1, month: m1, day: d1 }
        larger = { year: y2, month: m2, day: d2 }
        sign = 1
      } else {
        smaller = { year: y2, month: m2, day: d2 }
        larger = { year: y1, month: m1, day: d1 }
        sign = -1
      }
      let days =
        getDayOfYear(larger.year, larger.month, larger.day) -
        getDayOfYear(smaller.year, smaller.month, smaller.day)
      for (let year = smaller.year; year < larger.year; ++year) {
        days += isLeapYear(year) ? 366 : 365
      }
      let weeks = 0
      if (largestUnit === 'week') {
        weeks = Math.floor(days / 7)
        days %= 7
      }
      weeks *= sign
      days *= sign
      return { years: 0, months: 0, weeks, days }
    }
    default:
      throw new Error('assert not reached')
  }
}

export function differenceTime (
  h1,
  min1,
  s1,
  ms1,
  µs1,
  ns1,
  h2,
  min2,
  s2,
  ms2,
  µs2,
  ns2
) {
  let hours = h2 - h1
  let minutes = min2 - min1
  let seconds = s2 - s1
  let milliseconds = ms2 - ms1
  let microseconds = µs2 - µs1
  let nanoseconds = ns2 - ns1

  const sign = durationSign(
    0,
    0,
    0,
    0,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  )
  hours *= sign
  minutes *= sign
  seconds *= sign
  milliseconds *= sign
  microseconds *= sign
  nanoseconds *= sign

  let deltaDays = 0
  ;({
    deltaDays,
    hour: hours,
    minute: minutes,
    second: seconds,
    millisecond: milliseconds,
    microsecond: microseconds,
    nanosecond: nanoseconds
  } = balanceTime(
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  ))

  if (deltaDays !== 0) {
    throw new Error(
      'assertion failure in DifferenceTime: _bt_.[[Days]] should be 0'
    )
  }
  hours *= sign
  minutes *= sign
  seconds *= sign
  milliseconds *= sign
  microseconds *= sign
  nanoseconds *= sign

  return { hours, minutes, seconds, milliseconds, microseconds, nanoseconds }
}

export function differenceISODateTime (
  y1Param,
  mon1Param,
  d1Param,
  h1,
  min1,
  s1,
  ms1,
  µs1,
  ns1,
  y2,
  mon2,
  d2,
  h2,
  min2,
  s2,
  ms2,
  µs2,
  ns2,
  calendar,
  largestUnit,
  options = {}
) {
  let y1 = y1Param
  let mon1 = mon1Param
  let d1 = d1Param

  let { hours, minutes, seconds, milliseconds, microseconds, nanoseconds } =
    differenceTime(h1, min1, s1, ms1, µs1, ns1, h2, min2, s2, ms2, µs2, ns2)

  const timeSign = durationSign(
    0,
    0,
    0,
    0,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  )
  const dateSign = compareISODate(y2, mon2, d2, y1, mon1, d1)
  if (dateSign === -timeSign) {
    ;({
      year: y1,
      month: mon1,
      day: d1
    } = balanceISODate(y1, mon1, d1 - timeSign))
    ;({ hours, minutes, seconds, milliseconds, microseconds, nanoseconds } =
      balanceDuration(
        -timeSign,
        hours,
        minutes,
        seconds,
        milliseconds,
        microseconds,
        nanoseconds,
        largestUnit
      ))
  }

  const date1 = new DateTime(y1, mon1, d1, 0, 0, 0, 0, 0, 0, null, calendar)
  const date2 = new DateTime(y2, mon2, d2, 0, 0, 0, 0, 0, 0, null, calendar)
  const untilOptions = { ...options }
  untilOptions.largestUnit = largestUnit || 'day'
  // TODO untilOptions doesn't want to compile as it seems that smallestUnit is not clamped?
  // Type 'SmallestUnit<DateTimeUnit> | undefined' is not assignable to type
  //      'SmallestUnit<"year" | "month" | "day" | "week"> | undefined'.
  // Type '"hour"' is not assignable to type
  //      'SmallestUnit<"year" | "month" | "day" | "week"> | undefined'.ts(2345)
  let { years, months, weeks, days } = calendar.dateUntil(date1, date2, untilOptions)
  // Signs of date part and time part may not agree; balance them together
  ;({ days, hours, minutes, seconds, milliseconds, microseconds, nanoseconds } =
    balanceDuration(
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
      largestUnit
    ))

  return {
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  }
}

const units = new Map([
  ['nanosecond', 0],
  ['microsecond', 1],
  ['millisecond', 2],
  ['second', 3],
  ['minute', 4],
  ['hour', 5],
  ['day', 6],
  ['month', 7],
  ['year', 8]
])
const unitsArray = [...units.keys()]

export function differenceZonedDateTime (
  operation,
  zonedDateTime,
  otherParam,
  optionsParam
) {
  const sign = operation === 'since' ? -1 : 1
  let other = DateTime.isDateTime(otherParam)
    ? otherParam
    : DateTime.from(otherParam, {
      timeZone: zonedDateTime.timeZone,
      calendar: zonedDateTime.calendar
    })

  if (other.timeZone.id !== zonedDateTime.timeZone.id) {
    other = DateTime.from(other.epochNanoseconds, { timeZone: zonedDateTime.timeZone, calendar: zonedDateTime.calendar })
  }

  const calendar = zonedDateTime.calendar
  const otherCalendar = other.calendar
  const calendarId = calendar.id
  const otherCalendarId = otherCalendar.id
  if (calendarId !== otherCalendarId) {
    throw new RangeError(
      `cannot compute difference between dates of ${calendarId} and ${otherCalendarId} calendars`
    )
  }
  const options = optionsParam || {}
  const smallestUnit =
    typeof options.smallestUnit === 'string'
      ? options.smallestUnit
      : 'nanosecond'
  const smallestUnitIndex = units.get(smallestUnit)
  let largestUnit =
    typeof options.largestUnit === 'string' ? options.largestUnit : 'auto'
  if (largestUnit === 'auto') {
    largestUnit =
      smallestUnitIndex >= 5 ? unitsArray[smallestUnitIndex + 1] : 'hour'
  }
  if (units.get(largestUnit) < smallestUnitIndex) {
    throw new RangeError(
      `largestUnit ${largestUnit} cannot be smaller than smallestUnit ${smallestUnit}`
    )
  }

  const roundingIncrement = 1

  const ns1 = zonedDateTime.epochNanoseconds
  const ns2 = other.epochNanoseconds
  let years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  if (
    largestUnit !== 'year' &&
    largestUnit !== 'month' &&
    largestUnit !== 'week' &&
    largestUnit !== 'day'
  ) {
    // The user is only asking for a time difference, so return difference of instants.
    years = 0
    months = 0
    weeks = 0
    days = 0
    ;({ hours, minutes, seconds, milliseconds, microseconds, nanoseconds } =
      differenceInstant(
        ns1,
        ns2,
        roundingIncrement,
        // TODO this doesn't type-check as it includes >= day-size units
        // This is probably safe as the typing for ToSmallestTemporalUnit isn't
        // very good.
        smallestUnit,
        largestUnit
      ))
  } else {
    const timeZone = zonedDateTime.timeZone
    if (timeZone.id !== other.timeZone.id) {
      throw new RangeError(
        "When calculating difference between time zones, largestUnit must be 'hours' " +
          'or smaller because day lengths can vary between time zones due to DST or time zone offset changes.'
      )
    }
    const untilOptions = largestUnit
    ;({
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = differenceZonedDateTimeI(
      ns1,
      ns2,
      timeZone,
      calendar,
      largestUnit,
      untilOptions
    ))
    ;({
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = roundDuration(
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
      roundingIncrement,
      smallestUnit,
      'trunc',
      zonedDateTime
    ))
    ;({
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = adjustRoundedDurationDays(
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
      roundingIncrement,
      smallestUnit,
      'trunc',
      zonedDateTime
    ))
  }

  return new Duration(
    sign * years,
    sign * months,
    sign * weeks,
    sign * days,
    sign * hours,
    sign * minutes,
    sign * seconds,
    sign * milliseconds,
    sign * microseconds,
    sign * nanoseconds
  )
}

function differenceInstant (
  ns1,
  ns2,
  increment,
  smallestUnit,
  largestUnit,
  roundingMode
) {
  const diff = ns2 - ns1

  let hours = 0
  let minutes = 0
  let nanoseconds = Number(diff % THOUSAND)
  let microseconds = Number((diff / THOUSAND) % THOUSAND)
  let milliseconds = Number((diff / MILLION) % THOUSAND)
  let seconds = Number(diff / BILLION)

  ;({ hours, minutes, seconds, milliseconds, microseconds, nanoseconds } =
    roundDuration(
      0,
      0,
      0,
      0,
      0,
      0,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
      increment,
      smallestUnit,
      roundingMode
    ))

  return balanceDuration(
    0,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds,
    largestUnit
  )
}

function differenceZonedDateTimeI (
  ns1,
  ns2,
  timeZone,
  calendar,
  largestUnit,
  options
) {
  const nsDiff = ns2 - ns1
  if (nsDiff === ZERO) {
    return {
      years: 0,
      months: 0,
      weeks: 0,
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0,
      microseconds: 0,
      nanoseconds: 0
    }
  }

  const start = DateTime.from(ns1)
  const end = DateTime.from(ns2)
  const dtStart = DateTime.from(start.epochNanoseconds)
  const dtEnd = DateTime.from(end.epochNanoseconds)
  let { years, months, weeks, days } = differenceISODateTime(
    dtStart.year,
    dtStart.month,
    dtStart.day,
    dtStart.hour,
    dtStart.minute,
    dtStart.second,
    dtStart.millisecond,
    dtStart.microsecond,
    dtStart.nanosecond,
    dtEnd.year,
    dtEnd.month,
    dtEnd.day,
    dtEnd.hour,
    dtEnd.minute,
    dtEnd.second,
    dtEnd.millisecond,
    dtEnd.microsecond,
    dtEnd.nanosecond,
    calendar,
    largestUnit,
    options
  )
  const intermediateNs = addZonedDateTime(
    start,
    timeZone,
    calendar,
    years,
    months,
    weeks,
    0,
    0,
    0,
    0,
    0,
    0,
    0
  )
  // may disambiguate
  let timeRemainderNs = ns2 - intermediateNs
  const intermediate = DateTime.from(intermediateNs, { timeZone, calendar })
  ;({ nanoseconds: timeRemainderNs, days } = nanosecondsToDays(
    timeRemainderNs,
    intermediate
  ))

  // Finally, merge the date and time durations and return the merged result.
  const { hours, minutes, seconds, milliseconds, microseconds, nanoseconds } =
    balanceDuration(0, 0, 0, 0, 0, 0, Number(timeRemainderNs), 'hour')
  return {
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  }
}

export function roundDuration (
  yearsParam,
  monthsParam,
  weeksParam,
  daysParam,
  hoursParam,
  minutesParam,
  secondsParam,
  millisecondsParam,
  microsecondsParam,
  nanosecondsParam,
  increment,
  unit,
  roundingMode,
  relativeToParam = undefined
) {
  let years = yearsParam
  let months = monthsParam
  let weeks = weeksParam
  let days = daysParam
  let hours = hoursParam
  let minutes = minutesParam
  let seconds = secondsParam
  let milliseconds = millisecondsParam
  let microseconds = microsecondsParam
  let nanoseconds = BigInt(nanosecondsParam)
  let calendar, zdtRelative
  let relativeTo = null

  // First convert time units up to days, if rounding to days or higher units.
  // If rounding relative to a ZonedDateTime, then some days may not be 24h.
  // TS doesn't know that `dayLengthNs` is only used if the unit is day or
  // larger. We'll cast away `undefined` when it's used lower down below.
  let dayLengthNs
  if (
    unit === 'year' ||
    unit === 'month' ||
    unit === 'week' ||
    unit === 'day'
  ) {
    nanoseconds = totalDurationNanoseconds(
      0,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanosecondsParam,
      0
    )
    let intermediate
    let deltaDays
    let dayLength
    ;({
      days: deltaDays,
      nanoseconds,
      dayLengthNs: dayLength
    } = nanosecondsToDays(nanoseconds, intermediate))
    dayLengthNs = BigInt(dayLength)
    days += deltaDays
    hours = minutes = seconds = milliseconds = microseconds = 0
  }

  let total
  switch (unit) {
    case 'day': {
      // dayLengthNs is never undefined if unit is `day` or larger.
      const divisor = dayLengthNs
      nanoseconds = divisor * BigInt(days) + nanoseconds
      const rounded = roundNumberToIncrement(
        nanoseconds,
        Number(divisor * BigInt(increment)),
        roundingMode
      )

      total = Number(nanoseconds) / Number(divisor)
      days = Number(rounded / divisor)
      nanoseconds = ZERO
      break
    }
    case 'hour': {
      const divisor = 3600e9
      let allNanoseconds = BigInt(hours) * BigInt(3600e9)
      allNanoseconds = allNanoseconds + BigInt(minutes) * BigInt(60e9)
      allNanoseconds = allNanoseconds + BigInt(seconds) * BILLION
      allNanoseconds = allNanoseconds + BigInt(milliseconds) * MILLION
      allNanoseconds = allNanoseconds + BigInt(microseconds) * THOUSAND
      allNanoseconds = allNanoseconds + nanoseconds
      total = Number(allNanoseconds) / divisor
      const rounded = roundNumberToIncrement(
        allNanoseconds,
        divisor * increment,
        roundingMode
      )
      hours = Number(rounded / BigInt(divisor))
      nanoseconds = ZERO
      minutes = seconds = milliseconds = microseconds = 0
      break
    }
    case 'minute': {
      const divisor = 60e9
      let allNanoseconds = BigInt(minutes) * BigInt(60e9)
      allNanoseconds = allNanoseconds + BigInt(seconds) * BILLION
      allNanoseconds = allNanoseconds + BigInt(milliseconds) * MILLION
      allNanoseconds = allNanoseconds + BigInt(microseconds) * THOUSAND
      allNanoseconds = allNanoseconds + nanoseconds
      total = Number(allNanoseconds) / divisor
      const rounded = roundNumberToIncrement(
        allNanoseconds,
        divisor * increment,
        roundingMode
      )
      minutes = Number(rounded / BigInt(divisor))
      nanoseconds = ZERO
      seconds = milliseconds = microseconds = 0
      break
    }
    case 'second': {
      const divisor = 1e9
      let allNanoseconds = BigInt(seconds) * BILLION
      allNanoseconds = allNanoseconds + BigInt(milliseconds) * MILLION
      allNanoseconds = allNanoseconds + BigInt(microseconds) * THOUSAND
      allNanoseconds = allNanoseconds + nanoseconds
      total = Number(allNanoseconds) / divisor
      const rounded = roundNumberToIncrement(
        allNanoseconds,
        divisor * increment,
        roundingMode
      )
      seconds = Number(rounded / BigInt(divisor))
      nanoseconds = ZERO
      milliseconds = microseconds = 0
      break
    }
    case 'millisecond': {
      const divisor = 1e6
      let allNanoseconds = BigInt(milliseconds) * MILLION
      allNanoseconds = allNanoseconds + BigInt(microseconds) * THOUSAND
      allNanoseconds = allNanoseconds + nanoseconds
      total = Number(allNanoseconds) / divisor
      const rounded = roundNumberToIncrement(
        allNanoseconds,
        divisor * increment,
        roundingMode
      )
      milliseconds = Number(rounded / BigInt(divisor))
      nanoseconds = ZERO
      microseconds = 0
      break
    }
    case 'microsecond': {
      const divisor = 1e3
      let allNanoseconds = BigInt(microseconds) * THOUSAND
      allNanoseconds = allNanoseconds + nanoseconds
      total = Number(allNanoseconds) / divisor
      const rounded = roundNumberToIncrement(
        allNanoseconds,
        divisor * increment,
        roundingMode
      )
      microseconds = Number(rounded / BigInt(divisor))
      nanoseconds = ZERO
      break
    }
    case 'nanosecond': {
      total = Number(nanoseconds)
      nanoseconds = roundNumberToIncrement(nanoseconds, increment, roundingMode)
      break
    }
  }
  return {
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds: Number(nanoseconds),
    total
  }
}

function divmod (a, b) {
  const quotient = a / b
  const remainder = a % b
  return { quotient, remainder }
}

function abs (x) {
  if (x < ZERO) return x * NEGATIVE_ONE
  return x
}

function roundNumberToIncrement (quantity, increment, mode) {
  if (increment === 1) return quantity
  let { quotient, remainder } = divmod(quantity, BigInt(increment))
  if (remainder === ZERO) return quantity
  const sign = remainder < ZERO ? -1 : 1
  const tiebreaker = abs(remainder * BigInt(2))
  const tie = tiebreaker === BigInt(increment)
  const expandIsNearer = tiebreaker > BigInt(increment)
  switch (mode) {
    case 'ceil':
      if (sign > 0) quotient = quotient + BigInt(sign)
      break
    case 'floor':
      if (sign < 0) quotient = quotient + BigInt(sign)
      break
    case 'expand':
      // always expand if there is a remainder
      quotient = quotient + BigInt(sign)
      break
    case 'trunc':
      // no change needed, because divmod is a truncation
      break
    case 'halfCeil':
      if (expandIsNearer || (tie && sign > 0)) {
        quotient = quotient + BigInt(sign)
      }
      break
    case 'halfFloor':
      if (expandIsNearer || (tie && sign < 0)) {
        quotient = quotient + BigInt(sign)
      }
      break
    case 'halfExpand':
      // "half up away from zero"
      if (expandIsNearer || tie) {
        quotient = quotient + BigInt(sign)
      }
      break
    case 'halfTrunc':
      if (expandIsNearer) {
        quotient = quotient + BigInt(sign)
      }
      break
    case 'halfEven':
      if (expandIsNearer || (tie && Number(abs(quotient) % BigInt(2)) === 1)) {
        quotient = quotient + BigInt(sign)
      }
      break
  }
  return quotient * BigInt(increment)
}

export function adjustRoundedDurationDays (
  yearsParam,
  monthsParam,
  weeksParam,
  daysParam,
  hoursParam,
  minutesParam,
  secondsParam,
  millisecondsParam,
  microsecondsParam,
  nanosecondsParam,
  increment,
  unit,
  roundingMode,
  relativeTo
) {
  let years = yearsParam
  let months = monthsParam
  let weeks = weeksParam
  let days = daysParam
  let hours = hoursParam
  let minutes = minutesParam
  let seconds = secondsParam
  let milliseconds = millisecondsParam
  let microseconds = microsecondsParam
  let nanoseconds = nanosecondsParam
  if (
    unit === 'year' ||
    unit === 'month' ||
    unit === 'week' ||
    unit === 'day' ||
    (unit === 'nanosecond' && increment === 1)
  ) {
    return {
      years,
      months,
      weeks,
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    }
  }

  // There's one more round of rounding possible: if relativeTo is a
  // ZonedDateTime, the time units could have rounded up into enough hours
  // to exceed the day length. If this happens, grow the date part by a
  // single day and re-run exact time rounding on the smaller remainder. DO
  // NOT RECURSE, because once the extra hours are sucked up into the date
  // duration, there's no way for another full day to come from the next
  // round of rounding. And if it were possible (e.g. contrived calendar
  // with 30-minute-long "days") then it'd risk an infinite loop.
  let timeRemainderNs = totalDurationNanoseconds(
    0,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds,
    0
  )
  const direction = Math.sign(Number(timeRemainderNs))
  const dayStart = relativeTo.add({ years, months, weeks, days })
  const dayEnd = dayStart.add({ days: direction })
  const dayLengthNs = dayEnd - dayStart

  if ((timeRemainderNs - dayLengthNs) * BigInt(direction) >= ZERO) {
    ;({ years, months, weeks, days } = addDuration(
      years,
      months,
      weeks,
      days,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      direction,
      0,
      0,
      0,
      0,
      0,
      0,
      relativeTo
    ))
    timeRemainderNs = roundInstant(
      timeRemainderNs - dayLengthNs,
      increment,
      unit,
      roundingMode
    )
    ;({ hours, minutes, seconds, milliseconds, microseconds, nanoseconds } =
      balanceDuration(0, 0, 0, 0, 0, 0, Number(timeRemainderNs), 'hour'))
  }
  return {
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  }
}

export function addZonedDateTime (
  instant,
  timeZone,
  calendar,
  years,
  months,
  weeks,
  days,
  h,
  min,
  s,
  ms,
  µs,
  ns,
  options
) {
  // If only time is to be added, then use Instant math. It's not OK to fall
  // through to the date/time code below because compatible disambiguation in
  // the PlainDateTime=>Instant conversion will change the offset of any
  // ZonedDateTime in the repeated clock time after a backwards transition.
  // When adding/subtracting time units and not dates, this disambiguation is
  // not expected and so is avoided below via a fast path for time-only
  // arithmetic.
  // BTW, this behavior is similar in spirit to offset: 'prefer' in `with`.
  if (durationSign(years, months, weeks, days, 0, 0, 0, 0, 0, 0) === 0) {
    return addInstant(instant, h, min, s, ms, µs, ns)
  }

  // RFC 5545 requires the date portion to be added in calendar days and the
  // time portion to be added in exact time.
  const dt = DateTime.from(instant, { timeZone, calendar })
  const datePart = new DateTime(
    dt.year,
    dt.month,
    dt.day,
    0,
    0,
    0,
    0,
    0,
    0,
    null,
    calendar
  )
  const dateDuration = new Duration(
    years,
    months,
    weeks,
    days,
    0,
    0,
    0,
    0,
    0,
    0
  )
  const addedDate = calendar.dateAdd(datePart, dateDuration, options)
  const dtIntermediate = new DateTime(
    addedDate.year,
    addedDate.month,
    addedDate.day,
    dt.hour,
    dt.minute,
    dt.second,
    dt.millisecond,
    dt.microsecond,
    dt.nanosecond,
    timeZone,
    calendar
  )

  // Note that 'compatible' is used below because this disambiguation behavior
  // is required by RFC 5545.
  // const instantIntermediate = BuiltinTimeZoneGetInstantFor(timeZone, dtIntermediate, 'compatible');
  return addInstant(dtIntermediate.epochNanoseconds, h, min, s, ms, µs, ns)
}

function addInstant (epochNanoseconds, h, min, s, ms, µs, ns) {
  let sum = ZERO
  sum = sum + BigInt(ns)
  sum = sum + BigInt(µs) * THOUSAND
  sum = sum + BigInt(ms) * MILLION
  sum = sum + BigInt(s) * BILLION
  sum = sum + BigInt(min) * BigInt(60 * 1e9)
  sum = sum + BigInt(h) * BigInt(60 * 60 * 1e9)

  const result = epochNanoseconds + sum
  return result
}

export function roundInstant (epochNs, increment, unit, roundingMode) {
  // Note: NonNegativeModulo, but with BigInt
  let remainder = epochNs % BigInt(86400e9)
  if (remainder < ZERO) remainder = remainder + BigInt(86400e9)
  const wholeDays = epochNs - remainder
  const roundedRemainder = roundNumberToIncrement(
    remainder,
    nsPerTimeUnit[unit] * increment,
    roundingMode
  )
  return wholeDays + roundedRemainder
}

export function getDefaultLargestUnit (
  years,
  months,
  weeks,
  days,
  hours,
  minutes,
  seconds,
  milliseconds,
  microseconds,
  nanoseconds
) {
  if (years !== 0) {
    return 'year'
  }

  if (months !== 0) {
    return 'month'
  }

  if (weeks !== 0) {
    return 'week'
  }

  if (days !== 0) {
    return 'day'
  }

  if (hours !== 0) {
    return 'hour'
  }

  if (minutes !== 0) {
    return 'minute'
  }

  if (seconds !== 0) {
    return 'second'
  }

  if (milliseconds !== 0) {
    return 'millisecond'
  }

  if (microseconds !== 0) {
    return 'microsecond'
  }

  if (nanoseconds !== 0) {
    return 'nanosecond'
  }

  return 'nanosecond'
}

export function getLargestUnit (a, b) {
  return unitsArray[Math.max(units.get(a), units.get(b))]
}

export function getUnit (a) {
  return units.get(a)
}

export function addDuration (
  y1,
  mon1,
  w1,
  d1,
  h1,
  min1,
  s1,
  ms1,
  µs1,
  ns1,
  y2,
  mon2,
  w2,
  d2,
  h2,
  min2,
  s2,
  ms2,
  µs2,
  ns2,
  relativeTo
) {
  const largestUnit1 = getDefaultLargestUnit(
    y1,
    mon1,
    w1,
    d1,
    h1,
    min1,
    s1,
    ms1,
    µs1,
    ns1
  )
  const largestUnit2 = getDefaultLargestUnit(
    y2,
    mon2,
    w2,
    d2,
    h2,
    min2,
    s2,
    ms2,
    µs2,
    ns2
  )
  const largestUnit = getLargestUnit(largestUnit1, largestUnit2)

  let years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  if (!relativeTo) {
    if (
      largestUnit === 'year' ||
      largestUnit === 'month' ||
      largestUnit === 'week'
    ) {
      throw new RangeError(
        'relativeTo is required for years, months, or weeks arithmetic'
      )
    }
    years = months = weeks = 0
    ;({
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = balanceDuration(
      d1 + d2,
      BigInt(h1) + BigInt(h2),
      BigInt(min1) + BigInt(min2),
      BigInt(s1) + BigInt(s2),
      BigInt(ms1) + BigInt(ms2),
      BigInt(µs1) + BigInt(µs2),
      BigInt(ns1) + BigInt(ns2),
      largestUnit
    ))
  } else if (DateTime.isDateTime(relativeTo)) {
    const calendar = relativeTo.calendar

    const dateDuration1 = new Duration(y1, mon1, w1, d1, 0, 0, 0, 0, 0, 0)
    const dateDuration2 = new Duration(y2, mon2, w2, d2, 0, 0, 0, 0, 0, 0)
    const intermediate = calendar.dateAdd(relativeTo, dateDuration1)
    const end = calendar.dateAdd(intermediate, dateDuration2)

    const dateLargestUnit = 'day'
    const differenceOptions = Object.create(null)
    differenceOptions.largestUnit = dateLargestUnit
    ;({ years, months, weeks, days } = calendar.dateUntil(
      relativeTo,
      end,
      differenceOptions
    ))
    // Signs of date part and time part may not agree; balance them together
    ;({
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds
    } = balanceDuration(
      days,
      BigInt(h1) + BigInt(h2),
      BigInt(min1) + BigInt(min2),
      BigInt(s1) + BigInt(s2),
      BigInt(ms1) + BigInt(ms2),
      BigInt(µs1) + BigInt(µs2),
      BigInt(ns1) + BigInt(ns2),
      largestUnit
    ))
  } else {
    // relativeTo is a ZonedDateTime
    const timeZone = relativeTo.timeZone
    const calendar = relativeTo.calendar
    const intermediateNs = addZonedDateTime(
      DateTime.from(relativeTo.epochNanoseconds),
      timeZone,
      calendar,
      y1,
      mon1,
      w1,
      d1,
      h1,
      min1,
      s1,
      ms1,
      µs1,
      ns1
    )
    const endNs = addZonedDateTime(
      DateTime.from(intermediateNs),
      timeZone,
      calendar,
      y2,
      mon2,
      w2,
      d2,
      h2,
      min2,
      s2,
      ms2,
      µs2,
      ns2
    )
    if (
      largestUnit !== 'year' &&
      largestUnit !== 'month' &&
      largestUnit !== 'week' &&
      largestUnit !== 'day'
    ) {
      // The user is only asking for a time difference, so return difference of instants.
      years = 0
      months = 0
      weeks = 0
      days = 0
      ;({ hours, minutes, seconds, milliseconds, microseconds, nanoseconds } =
        differenceInstant(
          relativeTo.epochNanoseconds,
          endNs,
          1,
          'nanosecond',
          largestUnit,
          'halfExpand'
        ))
    } else {
      ;({
        years,
        months,
        weeks,
        days,
        hours,
        minutes,
        seconds,
        milliseconds,
        microseconds,
        nanoseconds
      } = differenceZonedDateTime(
        relativeTo.epochNanoseconds,
        endNs,
        timeZone,
        calendar,
        largestUnit,
        Object.create(null)
      ))
    }
  }

  rejectDuration(
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  )
  return {
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  }
}

export function differenceDateTime (
  operation,
  plainDateTime,
  otherParam,
  options = {}
) {
  const sign = operation === 'since' ? -1 : 1
  let other = DateTime.isDateTime(otherParam)
    ? otherParam
    : DateTime.from(otherParam)
  if (other.timeZone.id !== plainDateTime.timeZone.id) {
    other = DateTime.from(other.epochNanoseconds, {
      timeZone: plainDateTime.timeZone,
      calendar: plainDateTime.calendar
    })
  }
  const calendar = plainDateTime.calendar
  const otherCalendar = other.calendar
  const calendarId = calendar.id
  const otherCalendarId = otherCalendar.id
  if (calendarId !== otherCalendarId) {
    throw new RangeError(
      `cannot compute difference between dates of ${calendarId} and ${otherCalendarId} calendars`
    )
  }
  const smallestUnit =
    typeof options.smallestUnit === 'string'
      ? options.smallestUnit
      : 'nanosecond'
  const defaultLargestUnit = getLargestUnit('day', smallestUnit)
  let largestUnit = typeof options.largestUnit === 'string' ? options.largestUnit : 'auto'
  if (largestUnit === 'auto') largestUnit = defaultLargestUnit
  if (getLargestUnit(largestUnit, smallestUnit) !== largestUnit) {
    throw new RangeError(
      `largestUnit ${largestUnit} cannot be smaller than smallestUnit ${smallestUnit}`
    )
  }

  let {
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  } = differenceISODateTime(
    plainDateTime.year,
    plainDateTime.month,
    plainDateTime.day,
    plainDateTime.hour,
    plainDateTime.minute,
    plainDateTime.second,
    plainDateTime.millisecond,
    plainDateTime.microsecond,
    plainDateTime.nanosecond,
    other.year,
    other.month,
    other.day,
    other.hour,
    other.minute,
    other.second,
    other.millisecond,
    other.microsecond,
    other.nanosecond,
    calendar,
    largestUnit,
    options
  )

  const relativeTo = new DateTime(
    plainDateTime.year,
    plainDateTime.month,
    plainDateTime.day,
    0,
    0,
    0,
    0,
    0,
    0,
    plainDateTime.timeZone,
    calendar
  )
  ;({
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds
  } = roundDuration(
    years,
    months,
    weeks,
    days,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds,
    1,
    smallestUnit,
    'trunc',
    relativeTo
  ))
  ;({ days, hours, minutes, seconds, milliseconds, microseconds, nanoseconds } =
    balanceDuration(
      days,
      hours,
      minutes,
      seconds,
      milliseconds,
      microseconds,
      nanoseconds,
      largestUnit
    ))

  return new Duration(
    sign * years,
    sign * months,
    sign * weeks,
    sign * days,
    sign * hours,
    sign * minutes,
    sign * seconds,
    sign * milliseconds,
    sign * microseconds,
    sign * nanoseconds
  )
}

export function roundNanoseconds (
  epochNs,
  increment,
  unit,
  roundingMode
) {
  // Note: NonNegativeModulo, but with BigInt
  let remainder = epochNs % BigInt(86400e9)
  if (remainder < ZERO) remainder = remainder + BigInt(86400e9)
  const wholeDays = epochNs - remainder
  const roundedRemainder = roundNumberToIncrement(remainder, nsPerTimeUnit[unit] * increment, roundingMode)
  return wholeDays + roundedRemainder
}

export function roundTime (
  hour,
  minute,
  second,
  millisecond,
  microsecond,
  nanosecond,
  increment,
  unit,
  roundingMode,
  dayLengthNs = 86400e9
) {
  let quantity = ZERO
  switch (unit) {
    case 'day':
    case 'hour':
      quantity = BigInt(hour)
    // fall through
    case 'minute':
      quantity = (quantity * SIXTY) + BigInt(minute)
    // fall through
    case 'second':
      quantity = (quantity * SIXTY) + BigInt(second)
    // fall through
    case 'millisecond':
      quantity = (quantity * THOUSAND) + BigInt(millisecond)
    // fall through
    case 'microsecond':
      quantity = (quantity * THOUSAND) + BigInt(microsecond)
    // fall through
    case 'nanosecond':
      quantity = (quantity * THOUSAND) + BigInt(nanosecond)
  }
  const nsPerUnit = unit === 'day' ? dayLengthNs : nsPerTimeUnit[unit]
  const rounded = roundNumberToIncrement(quantity, nsPerUnit * increment, roundingMode)
  const result = Number(rounded / BigInt(nsPerUnit))
  switch (unit) {
    case 'day':
      return { deltaDays: result, hour: 0, minute: 0, second: 0, millisecond: 0, microsecond: 0, nanosecond: 0 }
    case 'hour':
      return balanceTime(result, 0, 0, 0, 0, 0)
    case 'minute':
      return balanceTime(hour, result, 0, 0, 0, 0)
    case 'second':
      return balanceTime(hour, minute, result, 0, 0, 0)
    case 'millisecond':
      return balanceTime(hour, minute, second, result, 0, 0)
    case 'microsecond':
      return balanceTime(hour, minute, second, millisecond, result, 0)
    case 'nanosecond':
      return balanceTime(hour, minute, second, millisecond, microsecond, result)
    default:
      throw new Error(`Invalid unit ${unit}`)
  }
}

export function differencePlainTime (
  operation,
  plainTime,
  otherParam,
  optionsParam
) {
  const sign = operation === 'since' ? -1 : 1
  const other = PlainTime.from(otherParam)
  const options = optionsParam || {}
  let largestUnit = options.largestUnit || 'auto'
  if (largestUnit === 'auto') largestUnit = 'hour'
  const smallestUnit = options.smallestUnit || 'nanosecond'
  if (getLargestUnit(largestUnit, smallestUnit) !== largestUnit) {
    throw new RangeError(`largestUnit ${largestUnit} cannot be smaller than smallestUnit ${smallestUnit}`);
  }
  let roundingMode = options.roundingMode || 'trunc'
  if (operation === 'since') roundingMode = negateTemporalRoundingMode(roundingMode)
  const MAX_INCREMENTS = {
    hour: 24,
    minute: 60,
    second: 60,
    millisecond: 1000,
    microsecond: 1000,
    nanosecond: 1000
  }
  const roundingIncrement = MAX_INCREMENTS[smallestUnit]
  let { hours, minutes, seconds, milliseconds, microseconds, nanoseconds } = differenceTime(
    plainTime.hour,
    plainTime.minute,
    plainTime.second,
    plainTime.millisecond,
    plainTime.microsecond,
    plainTime.nanosecond,
    other.hour,
    other.minute,
    other.second,
    other.millisecond,
    other.microsecond,
    other.nanosecond
  )
  ;({ hours, minutes, seconds, milliseconds, microseconds, nanoseconds } = roundDuration(
    0,
    0,
    0,
    0,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds,
    roundingIncrement,
    smallestUnit,
    roundingMode
  ))
  ;({ hours, minutes, seconds, milliseconds, microseconds, nanoseconds } = balanceDuration(
    0,
    hours,
    minutes,
    seconds,
    milliseconds,
    microseconds,
    nanoseconds,
    largestUnit
  ))

  return new Duration(
    0,
    0,
    0,
    0,
    sign * hours,
    sign * minutes,
    sign * seconds,
    sign * milliseconds,
    sign * microseconds,
    sign * nanoseconds
  )
}

function negateTemporalRoundingMode (roundingMode) {
  switch (roundingMode) {
    case 'ceil':
      return 'floor'
    case 'floor':
      return 'ceil'
    case 'halfCeil':
      return 'halfFloor'
    case 'halfFloor':
      return 'halfCeil'
    default:
      return roundingMode
  }
}
